import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import * as Spinners from '@Services/Spinners';
import RxSpinnerComponent from '@Components/Results/utils/rx-spinner.component';
import ShowMore from '@Components/Results/utils/ShowMore';
import SubstanceQualitativeTable from '@Components/Results/substances/tables/qualitative/SubstanceQualitativeTable';

function SubstanceQualitativeData({
  metadata,
  total,
  data: initialData,
  uniqueId,
  retrieveData,
  track,
  showTitle,
  initialItemsAmount = 7,
}) {
  const [data, setData] = useState(initialData);
  const [isShowLess, setIsShowLess] = useState(false);
  const cacheRef = useRef(null);
  const id = `${uniqueId}-qualitative`;

  const appendRows = (rows) => {
    setData((prevData) => [...prevData, ...rows]);
  };

  const showMore = async (firstItem, lastItem) => {
    if (cacheRef.current) {
      appendRows(cacheRef.current);
      cacheRef.current = null;
      return;
    }

    Spinners.activate(id);

    try {
      const rows = await retrieveData(firstItem, lastItem);
      appendRows(rows);
      if (track) {
        track(rows, 'showMoreBio', null, firstItem, lastItem);
      }
    } catch {
      // Handle error if needed
    } finally {
      Spinners.terminate(id);
    }
  };

  const showLess = () => {
    const newData = [...data];
    cacheRef.current = newData.splice(initialItemsAmount);
    setData(newData);
  };

  return (
    <div className="substance-results-qualitative" id={id}>
      {showTitle && (
        <div className="u-els-font-size-base u-els-line-height-default">
          <strong>Qualitative Results</strong>
        </div>
      )}

      <SubstanceQualitativeTable metadata={metadata} data={data} total={total} />

      <RxSpinnerComponent spinnerId={id} position="relative" />

      <div className={`rx-btn-group--center ${isShowLess ? 'show-less-sticky' : ''}`}>
        <ShowMore
          initial={initialItemsAmount}
          current={data.length}
          total={total}
          container={`#${id}`}
          moreHandler={({ firstItem, lastItem }) => {
            setIsShowLess(true);
            return showMore(firstItem, lastItem);
          }}
          lessHandler={() => {
            setIsShowLess(false);
            return showLess();
          }}
        />
      </div>
    </div>
  );
}

SubstanceQualitativeData.propTypes = {
  metadata: MetadataPropTypesShape,
  total: PropTypes.number.isRequired,
  data: PropTypes.array.isRequired,
  uniqueId: PropTypes.string.isRequired,
  retrieveData: PropTypes.func.isRequired,
  track: PropTypes.func,
  showTitle: PropTypes.bool,
  initialItemsAmount: PropTypes.number,
};

SubstanceQualitativeData.defaultProps = {
  initialItemsAmount: 7,
};

export default SubstanceQualitativeData;
